import { useCallback, useEffect, useRef } from 'react';
import type { Entity } from '../types';
import type { GoodsData } from '@banhala/ui-kit';
import type { FlatListProps } from 'react-native';
import { bridge, isNotNil } from '../../../../../utils';
import { AdAction } from '../../../../../utils/eventLogger/adLogger/types';

type ViewableItem = Parameters<
  NonNullable<FlatListProps<GoodsData & Entity>['onViewableItemsChanged']>
>[0]['viewableItems'][number];

/**
 * @param parentIsVisible carousel을 감싸는 부모(AtomicRenderer)의 viewport 가시성.
 * inner FlatList의 onViewableItemsChanged는 자체(가로) viewport 기준이라 부모가
 * carousel을 clip해도 전송되므로 emit 시점에 부모 가시성을 한 번 더 가드한다.
 * 또한 false→true 전이 시 캐시된 viewable 아이템을 재전송하여 카루셀이 막
 * 시야에 들어왔는데 자체 콜백이 안 도는 케이스(데이터 동일/재 mount 직후 등)를 보정.
 * @param isAwaitingDataChange AtomicRenderer가 필터 변경/pull-to-refresh 직후
 * 1.5초간 emit을 차단하는 윈도우. 데이터 swap + auto-scroll 애니메이션 도중
 * 일시적으로 parentIsVisible이 truthy가 되며 발생하는 spurious emit을 막기 위함.
 */
export const useLogDisplayGoods = (
  parentIsVisible?: boolean,
  isAwaitingDataChange?: boolean,
) => {
  const loggedSnos = useRef<Array<number>>([]);
  const lastViewableItemsRef = useRef<Array<ViewableItem>>([]);
  const parentIsVisibleRef = useRef(parentIsVisible);
  parentIsVisibleRef.current = parentIsVisible;
  const isAwaitingDataChangeRef = useRef(isAwaitingDataChange);
  isAwaitingDataChangeRef.current = isAwaitingDataChange;

  const emitForViewable = (viewableItems: Array<ViewableItem>) => {
    if (parentIsVisibleRef.current === false) return;
    if (isAwaitingDataChangeRef.current === true) return;
    viewableItems.forEach(({ isViewable, item }) => {
      if (!isViewable || item.sno == null) return;
      if (loggedSnos.current.includes(item.sno)) return;
      loggedSnos.current.push(item.sno);

      bridge.logAnalyticsEvent({
        channel: ['firebase', 'amplitude', 'ablylog', 'facebook'],
        name: 'DISPLAY_GOODS',
        analytics: item.logging.analytics,
        ably_only_log: item.logging.ably_only_log,
      });
      if (isNotNil(item.item.ad)) {
        bridge.logAdEvent({ action: AdAction.DISPLAY, ad: item.item.ad });
      }
    });
  };

  // 부모 가시성이 truthy로 바뀌는 시점 / awaiting 윈도우가 풀리는 시점에
  // 캐시 기반 재평가 → loggedSnos dedup으로 중복 emit 없이 시야 진입 시점의
  // visible 아이템만 보냄.
  useEffect(() => {
    if (parentIsVisible && !isAwaitingDataChange) {
      emitForViewable(lastViewableItemsRef.current);
    }
  }, [parentIsVisible, isAwaitingDataChange]);

  const resetDisplayGoods = useCallback(() => {
    loggedSnos.current = [];
    emitForViewable(lastViewableItemsRef.current);
  }, []);

  const logDisplayGoods = useCallback<
    NonNullable<FlatListProps<GoodsData & Entity>['onViewableItemsChanged']>
  >(({ viewableItems }) => {
    lastViewableItemsRef.current = viewableItems;
    emitForViewable(viewableItems);
  }, []);

  return { logDisplayGoods, resetDisplayGoods };
};
